import { morph } from '@theme/morph';

const PRODUCT_CARD_SECTION_ID = 'product-card-template';

export function findProductCardRoot(element) {
  if (!(element instanceof Element)) return null;

  const link = element.closest('product-card-link');
  if (link) return link;

  const listItem = element.closest('.resource-list__item');
  const linkInItem = listItem?.querySelector('product-card-link');
  if (linkInItem) return linkInItem;

  return element.closest('product-card') ?? listItem;
}

function hasRenderedCardContent(element) {
  if (!(element instanceof Element)) return false;

  const card = element.matches('product-card') ? element : element.querySelector('product-card');
  const content =
    element.querySelector('.product-card__content') ?? card?.querySelector('.product-card__content');

  if (!content) return false;

  return Boolean(
    content.querySelector(
      '.card-gallery, [ref="cardGallery"], product-price, product-stitching, colour-swatches-product-card, .text-block'
    ) || content.textContent.trim().length > 20
  );
}

function parseProductCardFromHtml(htmlString) {
  const doc = new DOMParser().parseFromString(htmlString, 'text/html');
  const section =
    doc.querySelector(`#shopify-section-${PRODUCT_CARD_SECTION_ID}`) ??
    doc.querySelector('#shopify-section-main');

  const root = section ?? doc;

  return root.querySelector('product-card-link') ?? root.querySelector('product-card');
}

async function fetchProductCardHtml(handle) {
  const root = window.Shopify?.routes?.root || '/';
  const urls = [
    `${root}products/${handle}?view=card`,
    `${root}products/${handle}?sections=${PRODUCT_CARD_SECTION_ID}`,
    `${root}products/${handle}?section_id=${PRODUCT_CARD_SECTION_ID}`,
  ];

  for (const url of urls) {
    try {
      const response = await fetch(url);
      if (!response.ok) continue;

      if (url.includes('sections=')) {
        const data = await response.json();
        const html = data.sections?.[PRODUCT_CARD_SECTION_ID] ?? data[PRODUCT_CARD_SECTION_ID];
        if (html) return html;
        continue;
      }

      return response.text();
    } catch (_) {
      // Try the next URL.
    }
  }

  throw new Error('Unable to fetch stitched product card');
}

function getProductCard(cardRoot) {
  if (!(cardRoot instanceof Element)) return null;
  return cardRoot.matches('product-card') ? cardRoot : cardRoot.querySelector('product-card');
}

function getCardContent(cardRoot) {
  return getProductCard(cardRoot)?.querySelector('.product-card__content') ?? null;
}

function getInfoContainer(cardRoot) {
  const content = getCardContent(cardRoot);
  if (!content) return null;
  return content.querySelector('.group-block-content') ?? content;
}

function findTitleBlock(container) {
  if (!(container instanceof Element)) return null;

  const price = container.querySelector('product-price');
  if (price?.previousElementSibling?.classList?.contains('text-block')) {
    return price.previousElementSibling;
  }

  return container.querySelector('.text-block');
}

function normalizeHandle(handle) {
  return handle.replace(/^\/products\//, '').replace(/^\//, '').split('?')[0];
}

function updateGallery(currentGallery, newGallery) {
  if (!(currentGallery instanceof Element) || !(newGallery instanceof Element)) return;

  const preservedAspectRatio =
    currentGallery.style.getPropertyValue('--gallery-aspect-ratio') ||
    getComputedStyle(currentGallery).getPropertyValue('--gallery-aspect-ratio').trim();
  const currentImage = currentGallery.querySelector('.product-media__image, .card-gallery img');
  const newImage = newGallery.querySelector('.product-media__image, .card-gallery img');

  if (currentImage instanceof HTMLImageElement && newImage instanceof HTMLImageElement) {
    // Never copy width/height — those distort portrait media-fit boxes on mobile.
    for (const attr of ['src', 'srcset', 'sizes', 'alt']) {
      const value = newImage.getAttribute(attr);
      if (value != null) {
        currentImage.setAttribute(attr, value);
      } else {
        currentImage.removeAttribute(attr);
      }
    }

    currentImage.removeAttribute('width');
    currentImage.removeAttribute('height');
    currentImage.style.removeProperty('width');
    currentImage.style.removeProperty('height');
    currentImage.style.objectFit = 'contain';

    const productMedia = currentImage.closest('.product-media');
    if (productMedia instanceof HTMLElement && preservedAspectRatio) {
      productMedia.style.setProperty('--gallery-aspect-ratio', preservedAspectRatio);
      productMedia.style.setProperty('--ratio', preservedAspectRatio);
    }

    const newProductId = newGallery.getAttribute('data-product-id');
    if (newProductId) currentGallery.setAttribute('data-product-id', newProductId);

    if (preservedAspectRatio) {
      currentGallery.style.setProperty('--gallery-aspect-ratio', preservedAspectRatio);
    }
    return;
  }

  morph(currentGallery, newGallery);

  if (preservedAspectRatio) {
    currentGallery.style.setProperty('--gallery-aspect-ratio', preservedAspectRatio);
  }
}

function updateBrandLogo(currentLogo, newLogo) {
  if (!(currentLogo instanceof Element) || !(newLogo instanceof Element)) return;

  const currentImg = currentLogo.querySelector('img');
  const newImg = newLogo.querySelector('img');

  if (currentImg instanceof HTMLImageElement && newImg instanceof HTMLImageElement) {
    for (const attr of ['src', 'srcset', 'sizes', 'alt', 'width', 'height']) {
      const value = newImg.getAttribute(attr);
      if (value != null) {
        currentImg.setAttribute(attr, value);
      } else {
        currentImg.removeAttribute(attr);
      }
    }
    return;
  }

  const currentPlaceholder = currentLogo.matches('[style*="height"]') ? currentLogo : currentLogo.querySelector('[style*="height"]');
  const newPlaceholder = newLogo.matches('[style*="height"]') ? newLogo : newLogo.querySelector('[style*="height"]');

  if (currentPlaceholder && newPlaceholder) {
    currentPlaceholder.style.height = newPlaceholder.style.height;
  }
}

function updateInventory(currentInventory, newInventory) {
  if (!(currentInventory instanceof Element) || !(newInventory instanceof Element)) return;

  const currentText = currentInventory.querySelector('.product-inventory__text');
  const newText = newInventory.querySelector('.product-inventory__text');
  const currentIcon = currentInventory.querySelector('.product-inventory__icon');
  const newIcon = newInventory.querySelector('.product-inventory__icon');

  if (currentText && newText) {
    currentText.textContent = newText.textContent;
  }

  if (currentIcon && newIcon) {
    currentIcon.className = newIcon.className;
  }

  const inventoryProductId = newInventory.getAttribute('data-product-id');
  if (inventoryProductId) currentInventory.setAttribute('data-product-id', inventoryProductId);
}

function updateCallouts(currentCallouts, newCallouts) {
  if (!(currentCallouts instanceof Element) || !(newCallouts instanceof Element)) return;
  morph(currentCallouts, newCallouts, { childrenOnly: true });
  currentCallouts.hidden = false;
}

function findBrandLogo(container) {
  if (!(container instanceof Element)) return null;

  return (
    container.querySelector('.icon-block') ??
    container.querySelector('.icon-block__media')?.closest('a, div, span') ??
    container.querySelector(':scope > div[style*="height"]')
  );
}

function syncStitchingRadioSelection(container, productHandle) {
  if (!(container instanceof Element)) return;

  const handle = normalizeHandle(productHandle);

  container
    .querySelectorAll('product-stitching input[type="radio"], colour-swatches-product-card input[type="radio"]')
    .forEach((input) => {
      const inputHandle = normalizeHandle(
        input.dataset.handle ||
          input.dataset.connectedProductUrl ||
          input.closest('[data-product-handle]')?.dataset.productHandle ||
          ''
      );
      input.checked = inputHandle === handle || inputHandle.endsWith(`/${handle}`);
    });
}

/**
 * Updates product card content in place so layout and typography stay stable.
 * @param {Element} currentRoot
 * @param {Element} newRoot
 * @param {string} productHandle
 */
function updateStitchedProductCardContent(currentRoot, newRoot, productHandle) {
  const currentCard = getProductCard(currentRoot);
  const newCard = getProductCard(newRoot);
  const currentContent = getCardContent(currentRoot);
  const newContent = getCardContent(newRoot);
  const currentInfo = getInfoContainer(currentRoot);
  const newInfo = getInfoContainer(newRoot);

  if (!currentCard || !newCard || !currentContent || !newContent || !currentInfo || !newInfo) {
    currentRoot.replaceWith(document.importNode(newRoot, true));
    return;
  }

  const currentLink =
    currentCard.querySelector('[ref="productCardLink"]') ?? currentCard.querySelector('.product-card__link');
  const newLink = newCard.querySelector('[ref="productCardLink"]') ?? newCard.querySelector('.product-card__link');

  if (currentLink instanceof HTMLAnchorElement && newLink instanceof HTMLAnchorElement && newLink.href) {
    currentLink.href = newLink.href;
  }

  const currentCardLink = currentRoot.matches('product-card-link')
    ? currentRoot
    : currentRoot.querySelector('product-card-link');
  const newCardLink = newRoot.matches('product-card-link') ? newRoot : newRoot.querySelector('product-card-link');

  if (currentCardLink instanceof HTMLElement && newCardLink instanceof HTMLElement) {
    const featuredUrl = newCardLink.getAttribute('data-featured-media-url');
    if (featuredUrl) currentCardLink.setAttribute('data-featured-media-url', featuredUrl);

    const productId = newCardLink.getAttribute('data-product-id');
    if (productId) currentCardLink.setAttribute('data-product-id', productId);
  }

  const newProductId = newCard.getAttribute('data-product-id');
  if (newProductId) currentCard.setAttribute('data-product-id', newProductId);

  const currentGallery = currentContent.querySelector('.card-gallery, [ref="cardGallery"]');
  const newGallery = newContent.querySelector('.card-gallery, [ref="cardGallery"]');
  if (currentGallery && newGallery) updateGallery(currentGallery, newGallery);

  const currentLogo = findBrandLogo(currentInfo);
  const newLogo = findBrandLogo(newInfo);
  if (currentLogo && newLogo) {
    updateBrandLogo(currentLogo, newLogo);
  }

  const currentTitle = findTitleBlock(currentInfo);
  const newTitle = findTitleBlock(newInfo);
  if (currentTitle && newTitle) {
    const currentText = currentTitle.querySelector('p, a, span, rte-formatter');
    const newText = newTitle.querySelector('p, a, span, rte-formatter');
    if (currentText && newText) {
      currentText.textContent = newText.textContent;
    } else {
      morph(currentTitle, newTitle, { childrenOnly: true });
    }
  }

  const currentPrice = currentInfo.querySelector('product-price');
  const newPrice = newInfo.querySelector('product-price');
  if (currentPrice && newPrice) {
    const currentPriceContainer = currentPrice.querySelector('[ref="priceContainer"]');
    const newPriceContainer = newPrice.querySelector('[ref="priceContainer"]');
    if (currentPriceContainer && newPriceContainer) {
      morph(currentPriceContainer, newPriceContainer, { childrenOnly: true });
    } else {
      morph(currentPrice, newPrice, { childrenOnly: true });
    }

    const priceProductId = newPrice.getAttribute('data-product-id');
    if (priceProductId) currentPrice.setAttribute('data-product-id', priceProductId);
  }

  const currentInventory = currentInfo.querySelector('product-inventory');
  const newInventory = newInfo.querySelector('product-inventory');
  if (currentInventory && newInventory) {
    updateInventory(currentInventory, newInventory);
  }

  const currentCallouts = currentInfo.querySelector('.product-details-badges');
  const newCallouts = newInfo.querySelector('.product-details-badges');
  if (currentCallouts && newCallouts) {
    updateCallouts(currentCallouts, newCallouts);
  }

  syncStitchingRadioSelection(currentInfo, productHandle);

  const currentWishlist = currentCard.querySelector('wishlist-button');
  const newWishlist = newCard.querySelector('wishlist-button');
  if (currentWishlist && newWishlist) {
    const variantId = newWishlist.getAttribute('data-variant-id');
    if (variantId) currentWishlist.setAttribute('data-variant-id', variantId);
  }

  const quickAdd = currentCard.querySelector('quick-add-component');
  if (quickAdd) quickAdd.cachedProductHtml = undefined;
}

/**
 * Replaces a product card with freshly rendered content for the selected stitched product.
 * Stays on the current page — no navigation.
 * @param {string} productHandle
 * @param {Element} element
 */
export async function replaceStitchedProductCard(productHandle, element) {
  const cardRoot = findProductCardRoot(element);
  if (!cardRoot || !productHandle) return;

  const handle = productHandle.replace(/^\/products\//, '').replace(/^\//, '');

  cardRoot.setAttribute('aria-busy', 'true');

  try {
    const htmlString = await fetchProductCardHtml(handle);
    const newCard = parseProductCardFromHtml(htmlString);

    if (newCard && hasRenderedCardContent(newCard)) {
      updateStitchedProductCardContent(cardRoot, newCard, handle);
    } else {
      console.warn('Stitched product card response was empty or invalid', { handle });
    }
  } catch (err) {
    console.error('Error fetching stitched product card:', err);
  } finally {
    if (cardRoot.isConnected) {
      cardRoot.removeAttribute('aria-busy');
    }
  }
}

function getStitchingItemFromEvent(event) {
  const target = event.target;
  if (!(target instanceof Element)) return null;

  const item = target.closest('.product_stitching_item_card');
  if (!item || !item.closest('.product-card, .resource-list__item')) return null;
  return item;
}

let lastStitchActivateKey = '';
let lastStitchActivateAt = 0;

function handleProductCardStitchingActivate(event) {
  const item = getStitchingItemFromEvent(event);
  if (!item) return;

  // Ignore multi-touch / stray move end events that are not taps.
  if (event.type === 'touchend' && 'touches' in event && event.touches.length > 0) return;

  const productHandle = item.dataset.productHandle?.replace(/^\/products\//, '');
  if (!productHandle) return;

  const now = Date.now();
  const activateKey = `${productHandle}:${item.getAttribute('data-block-id') || ''}`;
  // touchend often precedes click — only run swap once per tap.
  if (activateKey === lastStitchActivateKey && now - lastStitchActivateAt < 500) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    return;
  }

  lastStitchActivateKey = activateKey;
  lastStitchActivateAt = now;

  event.preventDefault();
  event.stopPropagation();
  event.stopImmediatePropagation();

  const stitchingRoot = item.closest('product-stitching, colour-swatches-product-card');
  if (stitchingRoot) {
    syncStitchingRadioSelection(stitchingRoot, productHandle);
  }

  replaceStitchedProductCard(productHandle, item);
}

export function initProductCardStitchingClicks() {
  if (initProductCardStitchingClicks.initialized) return;
  initProductCardStitchingClicks.initialized = true;

  // Capture click + touchend: some mobile browsers miss click when opacity:0 overlays sit under the card link.
  document.body.addEventListener('click', handleProductCardStitchingActivate, true);
  document.body.addEventListener('touchend', handleProductCardStitchingActivate, { capture: true, passive: false });

  document.body.addEventListener(
    'mouseenter',
    function (event) {
      const swatchFieldset = event.target.closest('.page-type-product');
      const textLabelHTML = document.getElementById('pdp-label-color-name');
      const textDefaultLabelHTML = document.getElementById('pdp-label-color-name-first')?.textContent;
      if (!swatchFieldset) return;
      if (!textLabelHTML) return;
      if (event.target.matches('.product_stitching_item input')) {
        textLabelHTML.textContent = event.target.dataset.colorName;
      } else if (!event.target.closest('.product_stitching_item')) {
        textLabelHTML.textContent = textDefaultLabelHTML;
      }
    },
    true
  );
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initProductCardStitchingClicks);
} else {
  initProductCardStitchingClicks();
}

class ColourSwatches_ProductCard extends HTMLElement {
  constructor() {
    super();
    this.products = [];
  }

  connectedCallback() {
    this.container = this.querySelector('.colour-swatches__container fieldset');
    this.settings = this.setSettings();
    this.addEventListener('click', this.#handleClick, true);

    if (!this.container || !this.settings) return;

    const hideIfEmpty = this.settings.hideIfEmpty;

    if (this.settings.groupTag) {
      fetch(`/collections/all/${encodeURIComponent(this.settings.groupTag)}?view=data`)
        .then((response) => response.json())
        .then((responseJson) => {
          this.products = responseJson;
          this.initSwatches(responseJson);
          this.hideIfEmpty(hideIfEmpty);

          const placeholder = this.querySelector(`#placeholder_swatches_${this.settings.product_id}`);
          if (placeholder) placeholder.hidden = true;
        })
        .catch((err) => {
          console.error('Error loading colour swatches:', err);
          this.hideIfEmpty(hideIfEmpty);
        });
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick, true);
  }

  #handleClick = (event) => {
    if (this.settings?.stitching_context === 'product_page') return;

    const input =
      event.target.closest('[swatch-input]') ??
      event.target.closest('.variant-option__button-label--has-swatch')?.querySelector('[swatch-input]');

    if (!input) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    const productHandle = input.getAttribute('data-handle');
    if (!productHandle) return;

    syncStitchingRadioSelection(this, productHandle);

    replaceStitchedProductCard(productHandle, event.target);
  };

  hideIfEmpty(hideIfEmpty) {
    if (!hideIfEmpty) return;

    const swatches = this.querySelectorAll('.colour-swatches__container input');
    if (swatches.length > 0) return;

    this.classList.add('swatch-empty--hide');
  }

  setSettings() {
    const settingsRawJson = this.querySelector('.swatches-settings');
    if (!settingsRawJson) return {};
    return JSON.parse(settingsRawJson.innerHTML);
  }

  initSwatches(products) {
    let html = '';
    products.forEach((product) => {
      if (this.settings.image_type == true) {
        const imgSrc = this.getProductImageSrc(product);
        html += `<label class="variant-option__button-label variant-option__button-label--has-swatch">
                    <input id="swatch-input-${product.id}"
                    swatch-input
                    data-href="/products/${product.handle}"
                    data-handle="${product.handle}"
                    type="radio"
                    value="${product.id}"
                    aria-label="${product.handle}"
                    data-input-id="1-0"
                    data-option-value-id="${product.id}"
                    data-option-available="${product.available}"
                    data-connected-product-url="${product.handle}"
                    data-variant-id="${product.variants[0].id}"
                    ${this.settings.product_handle == product.handle ? 'checked' : ''}
                    >
                    <span class="swatch ${this.settings.stitching_context == 'product_page' ? 'swatch--unscaled' : ''}" style="--swatch-background: url(${imgSrc});"></span>
                    ${
                      !product.available
                        ? `
                      <svg
                        width="100%"
                        height="100%"
                        viewBox="0 0 100 46"
                        preserveAspectRatio="xMidYMid slice"
                      >
                        <line x1="100" y1="0" x2="0" y2="46" vector-effect="non-scaling-stroke" />
                      </svg>
                    `
                        : ''
                    }
                  </label>`;
      } else {
        const color_metafield = product.color_swatch_metafield;
        const image_swatch_metaobject = product.image_swatch_metaobject;
        html += `<label class="variant-option__button-label variant-option__button-label--has-swatch">
                    <input id="swatch-input-${product.id}" swatch-input  data-href="/products/${product.handle}"
                    data-handle="${product.handle}"
                    type="radio" value="${product.id}" aria-label="${product.handle}"
                    data-input-id="1-0" data-option-value-id="4635978924349"
                    data-option-available="${product.available}"
                    data-connected-product-url="${product.handle}"
                    data-variant-id="${product.variants[0].id}"
                    ${this.settings.product_handle == product.handle ? 'checked' : ''}
                    >
                    <span class="swatch ${this.settings.stitching_context == 'product_page' ? 'swatch--unscaled' : ''}"
                    style="${image_swatch_metaobject && image_swatch_metaobject !== '' ? `--swatch-background: url(${image_swatch_metaobject})` : `--swatch-background: ${color_metafield}`}"></span>
                    ${
                      !product.available
                        ? `
                      <svg
                        width="100%"
                        height="100%"
                        viewBox="0 0 100 46"
                        preserveAspectRatio="xMidYMid slice"
                      >
                        <line x1="100" y1="0" x2="0" y2="46" vector-effect="non-scaling-stroke" />
                      </svg>
                    `
                        : ''
                    }
                  </label>`;
      }
    });

    if (html) {
      this.container.innerHTML = html;
    }
  }

  getProductImageSrc(product) {
    let imageSrc = product.featured_image;
    switch (this.settings.image_type) {
      case 'color_swatch': {
        const colourTag = product.tags.find((tag) => tag.indexOf(this.settings.colourTagPrefix) > -1);
        if (colourTag) {
          const colour = colourTag.replace(this.settings.colourTagPrefix, '').toLowerCase();
          imageSrc = colour;
        }
        break;
      }
      case 'first_variant_image': {
        const firstVariantImage = product.variants[0].image;
        if (firstVariantImage) imageSrc = firstVariantImage;
        break;
      }
      case 'last_variant_image': {
        const lastVariantImg = product.variants[product.variants.length - 1].image;
        if (lastVariantImg) imageSrc = lastVariantImg;
        break;
      }
      default:
        break;
    }
    return imageSrc;
  }
}

if (!customElements.get('colour-swatches-product-card')) {
  customElements.define('colour-swatches-product-card', ColourSwatches_ProductCard);
}
